import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, LogOut, ArrowLeft, FileText, CheckCircle2, XCircle,
  Clock, AlertTriangle, Ban,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const card = "bg-white border border-neutral-200 rounded-2xl shadow-sm";

const STATUS_META = {
  pending: { label: "Pending", icon: Clock, cls: "bg-amber-100 text-amber-800" },
  verified: { label: "Verified", icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rejected", icon: Ban, cls: "bg-red-100 text-red-700" },
  expired: { label: "Licence expired", icon: AlertTriangle, cls: "bg-orange-100 text-orange-800" },
};

function Badge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${m.cls}`} data-testid="admin-sp-badge">
      <Icon size={12} /> {m.label}
    </span>
  );
}

function DocViewer({ doc }) {
  const url = doc.file_url || "";
  const isImg = url.startsWith("data:image");
  const isPdf = url.startsWith("data:application/pdf");
  return (
    <div className="mt-2">
      <p className="text-sm font-semibold text-neutral-800 flex items-center gap-1">
        <FileText size={14} /> {doc.label || doc.document_type} <span className="text-neutral-400 font-normal">· {doc.document_type.replace("_", " ")}</span>
      </p>
      {isImg && <img src={url} alt={doc.label} className="mt-2 rounded-lg border border-neutral-200 max-h-72 object-contain w-full bg-neutral-50" />}
      {isPdf && <iframe src={url} title={doc.label} className="mt-2 w-full h-72 rounded-lg border border-neutral-200" />}
      {!isImg && !isPdf && <a href={url} download={doc.label} className="mt-1 inline-block text-sm text-blue-600 underline">Download file</a>}
    </div>
  );
}

function Detail({ id, onBack, onChanged }) {
  const [sp, setSp] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/specialists/${id}`).then(({ data }) => setSp(data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (kind) => {
    if (kind === "reject" && !notes.trim()) return toast.error("Please add a reason for rejection.");
    setBusy(true);
    try {
      await api.post(`/admin/specialists/${id}/${kind}`, kind === "reject" ? { notes } : {});
      toast.success(kind === "verify" ? "Specialist verified." : "Application rejected.");
      onChanged();
      onBack();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!sp) return <div className={`${card} h-40 animate-pulse`} />;

  return (
    <div data-testid="admin-sp-detail">
      <button data-testid="admin-detail-back" onClick={onBack} className="flex items-center gap-1 text-sm text-neutral-500 mb-3 min-h-[44px]">
        <ArrowLeft size={16} /> Back to list
      </button>
      <div className={`${card} p-5`}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-head text-xl font-extrabold text-neutral-900">{sp.full_name}</h2>
            <p className="text-sm text-neutral-600">{sp.specialty} · {sp.country}</p>
          </div>
          <Badge status={sp.verification_status} />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-1 text-sm text-neutral-700">
          <p><span className="text-neutral-400">Email:</span> {sp.email}</p>
          <p><span className="text-neutral-400">Languages:</span> {(sp.languages || []).join(", ") || "—"}</p>
          <p><span className="text-neutral-400">Licence:</span> {sp.licence_number} · {sp.licence_body}</p>
          <p><span className="text-neutral-400">Issued:</span> {sp.licence_issue_date} ({sp.licence_issue_country})</p>
          <p><span className="text-neutral-400">Expiry:</span> {sp.licence_no_expiry ? "No expiry" : sp.licence_expiry_date}</p>
          <p><span className="text-neutral-400">Fee:</span> {sp.review_fee} {sp.currency} · {sp.avg_turnaround_days}-day turnaround</p>
          {sp.credentials_summary && <p><span className="text-neutral-400">Credentials:</span> {sp.credentials_summary}</p>}
          {sp.verified_by && <p><span className="text-neutral-400">Reviewed by:</span> {sp.verified_by}</p>}
        </div>
      </div>

      <div className={`${card} p-5 mt-4`}>
        <h3 className="font-head font-bold text-neutral-900 mb-1">Credential documents</h3>
        {sp.documents?.length ? sp.documents.map((d) => <DocViewer key={d.id} doc={d} />)
          : <p className="text-sm text-neutral-500">No documents uploaded.</p>}
      </div>

      {sp.verification_status !== "verified" && (
        <div className={`${card} p-5 mt-4`}>
          <label className="block text-sm font-medium text-neutral-600 mb-1.5">Rejection reason (required to reject)</label>
          <textarea data-testid="admin-reject-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            className="w-full border border-neutral-300 rounded-xl px-3 py-2.5 text-neutral-900 focus:outline-none focus:border-neutral-900"
            placeholder="e.g. Licence document is unreadable." />
          <div className="flex gap-3 mt-3">
            <button data-testid="admin-verify-btn" onClick={() => act("verify")} disabled={busy}
              className="flex-1 rounded-full bg-emerald-600 text-white font-bold px-4 py-3 min-h-[48px] disabled:opacity-60 flex items-center justify-center gap-2">
              <CheckCircle2 size={16} /> Verify
            </button>
            <button data-testid="admin-reject-btn" onClick={() => act("reject")} disabled={busy}
              className="flex-1 rounded-full bg-red-600 text-white font-bold px-4 py-3 min-h-[48px] disabled:opacity-60 flex items-center justify-center gap-2">
              <XCircle size={16} /> Reject
            </button>
          </div>
        </div>
      )}
      {sp.verification_status === "verified" && (
        <button data-testid="admin-revoke-btn" onClick={() => act("reject")} disabled={busy}
          className="mt-4 w-full rounded-full border border-red-300 text-red-600 font-bold px-4 py-3 min-h-[48px] disabled:opacity-60">
          Revoke verification
        </button>
      )}
    </div>
  );
}

const FILTERS = ["pending", "verified", "expired", "rejected", "all"];

export default function AdminSpecialists() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [list, setList] = useState(null);
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(null);

  const loadCounts = useCallback(() => {
    api.get("/admin/specialists/counts").then(({ data }) => setCounts(data)).catch(() => {});
  }, []);
  const loadList = useCallback((f) => {
    setList(null);
    api.get(`/admin/specialists?status=${f}`).then(({ data }) => setList(data)).catch(() => setList([]));
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("pd_token")) { navigate("/admin/login", { replace: true }); return; }
    api.get("/admin/me")
      .then(() => { setReady(true); loadCounts(); loadList("pending"); })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate, loadCounts, loadList]);

  const refresh = () => { loadCounts(); loadList(filter); };
  const logout = () => { localStorage.removeItem("pd_token"); navigate("/admin/login", { replace: true }); };

  if (!ready) {
    return (
      <div className="min-h-screen w-full flex justify-center bg-neutral-100">
        <div className="w-full max-w-[430px] min-h-screen bg-white p-6">
          <div className="h-40 bg-neutral-100 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex justify-center bg-neutral-100">
      <div className="relative w-full max-w-[430px] min-h-screen bg-neutral-50 flex flex-col">
        <header className="px-6 pt-8 pb-3 flex items-center justify-between bg-white border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-neutral-900 text-white">
              <ShieldCheck size={20} />
            </div>
            <div>
              <p className="font-head font-extrabold text-neutral-900 leading-tight">Specialist verification</p>
              <p className="text-xs text-neutral-500">Admin review</p>
            </div>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="text-neutral-500 flex items-center gap-1 text-sm min-h-[44px]">
            <LogOut size={16} />
          </button>
        </header>

        <div className="flex-1 px-6 py-4">
          {open ? (
            <Detail id={open} onBack={() => setOpen(null)} onChanged={refresh} />
          ) : (
            <>
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="admin-filters">
                {FILTERS.map((f) => (
                  <button key={f} data-testid={`admin-filter-${f}`} onClick={() => { setFilter(f); loadList(f); }}
                    className={`whitespace-nowrap px-3 py-2 rounded-full text-sm font-semibold border min-h-[40px] ${filter === f ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-neutral-300"}`}>
                    {f[0].toUpperCase() + f.slice(1)}{counts[f] != null ? ` (${counts[f]})` : ""}
                  </button>
                ))}
              </div>

              {list === null ? (
                <div className={`${card} h-24 animate-pulse mt-3`} />
              ) : list.length === 0 ? (
                <div className={`${card} p-6 text-center mt-3`} data-testid="admin-empty">
                  <p className="text-neutral-800 font-bold">No {filter === "all" ? "" : filter} specialists</p>
                  <p className="text-sm text-neutral-500 mt-1">Nothing to review here right now.</p>
                </div>
              ) : (
                <div className="space-y-3 mt-3" data-testid="admin-sp-list">
                  {list.map((sp) => (
                    <button key={sp.id} data-testid={`admin-sp-${sp.id}`} onClick={() => setOpen(sp.id)}
                      className={`${card} p-4 w-full text-left hover:border-neutral-400 transition-colors`}>
                      <div className="flex items-center justify-between">
                        <p className="font-head font-bold text-neutral-900">{sp.full_name}</p>
                        <Badge status={sp.verification_status} />
                      </div>
                      <p className="text-sm text-neutral-600 mt-1">{sp.specialty} · {sp.country}</p>
                      <p className="text-xs text-neutral-400 mt-1">{sp.document_count} document(s) · applied {(sp.submitted_at || "").slice(0, 10)}</p>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
